import type { ToolPersonProfile, ToolSources } from "#types/types.ts";
import type { CustomEvent as AGUICustomEvent } from "@ag-ui/core";

/**
 * The `CUSTOM` event name sources travel under.
 *
 * `wlai_` namespaced: `pydantic_ai_*` is reserved by pydantic-ai and `ag-ui` is reserved by the
 * protocol itself. Must stay in lockstep with `WLAI_SOURCES_EVENT_NAME` in core-python's
 * `native_wire_events.py`.
 */
export const WLAI_SOURCES_EVENT_NAME = "wlai_sources";

/**
 * `ACTIVITY_SNAPSHOT` types that are not statuses; every other activity type names a status. Must stay in
 * lockstep with core-python's `SKILL_LOADED_ACTIVITY_TYPE` (`status_activity.py`) and
 * `GENERATED_FILE_ACTIVITY_TYPE` (`generated_file.py`).
 */
export const SKILL_LOADED_ACTIVITY_TYPE = "skill_loaded";
export const GENERATED_FILE_ACTIVITY_TYPE = "generated_file";

/** The interrupt `reason` of a question the assistant asks. Must stay in lockstep with core-python's question interrupt. */
export const WLAI_QUESTION_INTERRUPT_REASON = "wlai_question";

/**
 * The interrupt `reason` of a request to connect integrations, raised on the web. Must stay in lockstep with
 * `CONNECTION_INTERRUPT_REASON` in core-python's `interrupt_builder.py`.
 */
export const WLAI_INTEGRATION_CONNECTION_INTERRUPT_REASON = "wlai_integration_connection";

/**
 * A `CUSTOM` event carrying one tool call's sources.
 *
 * The payload type is generated from the contract core-python publishes; it is never re-declared here.
 */
export type WlaiSourcesCustomEvent = AGUICustomEvent & {
    name: typeof WLAI_SOURCES_EVENT_NAME;
    value: ToolSources;
};

/** Narrows an arbitrary `CUSTOM` event to the sources event. */
export const isWlaiSourcesEvent = (event: AGUICustomEvent): event is WlaiSourcesCustomEvent => {
    return event.name === WLAI_SOURCES_EVENT_NAME;
};

/**
 * The `CUSTOM` event name the About People card travels under. Must stay in lockstep with
 * `WLAI_PERSON_PROFILE_EVENT_NAME` in core-python's `native_wire_events.py`.
 */
export const WLAI_PERSON_PROFILE_EVENT_NAME = "wlai_person_profile";

/** A `CUSTOM` event carrying the person profile one tool call rendered. */
export type WlaiPersonProfileCustomEvent = AGUICustomEvent & {
    name: typeof WLAI_PERSON_PROFILE_EVENT_NAME;
    value: ToolPersonProfile;
};

/** Narrows an arbitrary `CUSTOM` event to the person-profile event. */
export const isWlaiPersonProfileEvent = (event: AGUICustomEvent): event is WlaiPersonProfileCustomEvent => {
    return event.name === WLAI_PERSON_PROFILE_EVENT_NAME;
};

/**
 * core-python names every page action's tool `<prefix><action>` and passes the action's content as the
 * call's arguments, so the call itself is the page-action response. Must stay in lockstep with
 * `PAGE_ACTION_TOOL_PREFIX` in core-python's `page_action_toolset.py`.
 */
export const PAGE_ACTION_TOOL_PREFIX = "page_action_";

/**
 * The tool result core-python returns once a page action ran. A retried call (malformed arguments, an
 * unknown action) also gets a TOOL_CALL_RESULT, carrying the retry prompt instead, so only this result
 * makes a call a page-action response. Must stay in lockstep with `PageActionToolset.call_tool`'s return.
 */
export const PAGE_ACTION_EXECUTED_RESULT = "Page action executed.";
